import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import { fmt, dateFmt } from "../helpers/format"

const LettrageModal = ({ installment, transactions, onClose, onConfirm }) => {
  const [selectedId, setSelectedId] = useState(null)
  const [query, setQuery]           = useState("")

  const remaining = installment?.remaining_cents || 0

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (transactions || []).filter(txn => {
      if (!q) return true
      const haystack = [txn.counterpart, txn.reference, txn.description].join(" ").toLowerCase()
      return haystack.includes(q)
    })
  }, [transactions, query])

  // Score: prefer exact amount, then close amount, then most recent
  const sorted = useMemo(() => {
    return [...candidates].sort((a, b) => {
      const aDiff = Math.abs(a.amount_cents - remaining)
      const bDiff = Math.abs(b.amount_cents - remaining)
      if (aDiff !== bDiff) return aDiff - bDiff
      return (b.value_date || "").localeCompare(a.value_date || "")
    })
  }, [candidates, remaining])

  if (!installment) return null

  return (
    <Modal isOpen={true} onClose={onClose} title={I18n.t("projects.lettrage.title")} size="lg">
      <div className="px-5 py-4 space-y-4">
        <div className="text-[12.5px] text-slate-600">
          {I18n.t("projects.lettrage.installment_due")}
          {" "}
          <strong>{dateFmt(installment.due_date)}</strong>
          {" · "}
          {I18n.t("projects.lettrage.remaining")}
          {" "}
          <strong className="text-slate-900">
            {new Intl.NumberFormat("fr-FR", { style: "currency", currency: installment.currency || "EUR" })
              .format(remaining / 100)}
          </strong>
        </div>

        <input
          type        = "search"
          value       = {query}
          onChange    = {e => setQuery(e.target.value)}
          placeholder = {I18n.t("projects.lettrage.search_placeholder")}
          className   = "w-full h-9 px-3 border border-slate-300 rounded text-[13px]"
        />

        {sorted.length === 0 ? (
          <div className="py-8 text-center text-[12.5px] text-slate-500">
            {I18n.t("projects.lettrage.empty")}
          </div>
        ) : (
          <ul className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {sorted.map(txn => {
              const match = txn.amount_cents === remaining
              return (
                <li key={txn.id}>
                  <label className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 ${selectedId === txn.id ? "bg-blue-50" : ""}`}>
                    <input
                      type     = "radio"
                      name     = "bank_transaction"
                      checked  = {selectedId === txn.id}
                      onChange = {() => setSelectedId(txn.id)}
                      className= "h-4 w-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[13px] font-medium text-slate-900 truncate">
                          {txn.counterpart || I18n.t("projects.lettrage.unknown_counterpart")}
                        </span>
                        <span className={`text-[13px] font-semibold tabular-nums ${match ? "text-emerald-700" : "text-slate-900"}`}>
                          {txn.amount?.formatted}
                          {match && <FontAwesomeIcon icon="check" className="ml-1.5 text-emerald-600" />}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 truncate mt-0.5">
                        {dateFmt(txn.value_date)}
                        {txn.reference && <> · {txn.reference}</>}
                      </div>
                    </div>
                  </label>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>{I18n.t("button.cancel")}</Button>
        <Button
          variant  = "primary"
          disabled = {!selectedId}
          onClick  = {() => onConfirm(selectedId)}
        >
          <FontAwesomeIcon icon="link" /> {I18n.t("projects.lettrage.confirm")}
        </Button>
      </div>
    </Modal>
  )
}

export default LettrageModal
